import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom'
import { lazy, Suspense } from 'react'

import {
  HomePage,
  DashboardPage,
  ProjectDetailPage,
  ProjectsListPage,
  CreateProjectPage,
  OnboardProjectPage,
  CollectorsPage,
  SearchPage,
  SubjectsPage,
  EpisodesPage,
  ClaimsReviewPage,
  AnalyticsPage,
  ProceduralMemoryPage,
  KnowledgeBasePage,
  MemoryModelPage,
  TokenAnalyticsPage,
  CodeHealthPage,
  TodoListPage,
  IdeasPage,
  IdeaDetailPage,
  RemindersPage,
  PRDsPage,
  PromptLibraryPage,
  DispatchPage,
  AccessControlPage,
  TelemetryPoliciesPage,
  SessionSummariesPage,
  ModelCapabilitiesPage,
  IntegrationsPage,
  NotFoundPage,
  CCSupport,
  SessionDetailPage,
  IntakePage,
  UsagePage,
} from '@/routes/lazyPages'

// Layout components
import AppLayout from '@/layouts/AppLayout'
import LoadingFallback from '@/components/LoadingFallback'

// Route shims small enough to load eagerly
import IntakeItemRedirect from '@/pages/intake/IntakeItemRedirect'

// Error boundary
import ErrorBoundary from '@/components/ErrorBoundary'

// Page titles use "<Page name> - Alveary" (TODO-1660). Home is the Alveary
// conversation itself, so its title is the product name alone.
// The single DocumentTitle owner resolves metadata; redirects use their destination.
import type { RouteHandle } from '@/routes/routeHandle'

// Wrap lazy loaded components with Suspense
// Bare ComponentType (props default to {}) rather than ComponentType<any>: every
// call site renders <Component /> with no props, so that is exactly what the
// parameter needs to accept. ComponentType<never> does not work here — React
// declares LazyExoticComponent<T extends ComponentType<any>>, and `never` in the
// prop position fails both that constraint and the JSX call.
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType>) => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Component />
    </Suspense>
  )
}

// Route definitions with TypeScript
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        index: true,
        element: withSuspense(HomePage),
        handle: {
          title: 'Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'dashboard',
        element: withSuspense(DashboardPage),
        handle: {
          title: 'Dashboard - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'projects',
        children: [
          {
            index: true,
            element: withSuspense(ProjectsListPage),
            handle: {
              title: 'Projects - Alveary',
            } satisfies RouteHandle,
          },
          {
            path: 'new',
            element: withSuspense(CreateProjectPage),
            handle: {
              title: 'Create Project - Alveary',
            } satisfies RouteHandle,
          },
          {
            path: 'onboard',
            element: withSuspense(OnboardProjectPage),
            handle: {
              title: 'Onboard Project - Alveary',
            } satisfies RouteHandle,
          },
          {
            path: ':projectId',
            element: withSuspense(ProjectDetailPage),
            handle: {
              title: 'Project Details - Alveary',
            } satisfies RouteHandle,
          },
          {
            path: ':projectId/sessions/:sessionId',
            element: withSuspense(SessionDetailPage),
            handle: {
              title: 'Session Details - Alveary',
            } satisfies RouteHandle,
          },
        ],
      },
      {
        path: 'analytics',
        element: withSuspense(AnalyticsPage),
        handle: {
          title: 'Analytics - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'analytics/tokens',
        element: withSuspense(TokenAnalyticsPage),
        handle: {
          title: 'Token Analytics - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'usage',
        element: withSuspense(UsagePage),
        handle: {
          title: 'Usage - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'procedural-memory',
        element: withSuspense(ProceduralMemoryPage),
        handle: {
          title: 'Procedural Memory - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'knowledge-base',
        element: withSuspense(KnowledgeBasePage),
        handle: {
          title: 'Knowledge Base - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'memory-model',
        element: withSuspense(MemoryModelPage),
        handle: {
          title: 'Memory Model - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'code-health',
        element: withSuspense(CodeHealthPage),
        handle: {
          title: 'Code Health - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'todos',
        element: withSuspense(TodoListPage),
        handle: {
          title: 'TODOs - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'ideas',
        element: withSuspense(IdeasPage),
        handle: {
          title: 'Ideas - Alveary',
        } satisfies RouteHandle,
      },
      // One literal rather than a nested child: the backend's brief-link
      // contract matches a link's segments against each declared path.
      {
        path: 'ideas/:ideaId',
        element: withSuspense(IdeaDetailPage),
        handle: {
          title: 'Idea - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'reminders',
        element: withSuspense(RemindersPage),
        handle: {
          title: 'Reminders - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'intake',
        children: [
          {
            index: true,
            element: withSuspense(IntakePage),
            handle: {
              title: 'Customer Intake - Alveary',
            } satisfies RouteHandle,
          },
          // LEGACY: the pre-console intake pages lived at their own routes.
          // Kept for one release as redirects so shared links and bookmarks
          // land on the board with the same surface open; the console board
          // owns every intake surface through its URL overlay params.
          {
            path: 'new',
            element: <Navigate to="/intake?new=1" replace />,
          },
          {
            path: ':itemId',
            element: <IntakeItemRedirect />,
          },
          {
            path: ':itemId/edit',
            element: <IntakeItemRedirect />,
          },
        ],
      },
      {
        path: 'prds',
        element: withSuspense(PRDsPage),
        handle: {
          title: 'PRDs - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'collectors',
        element: withSuspense(CollectorsPage),
        handle: {
          title: 'Collectors - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'search',
        element: withSuspense(SearchPage),
        handle: {
          title: 'Search - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'subjects',
        element: withSuspense(SubjectsPage),
        handle: {
          title: 'Subjects - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'episodes',
        element: withSuspense(EpisodesPage),
        handle: {
          title: 'Episodes - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'claims',
        element: withSuspense(ClaimsReviewPage),
        handle: {
          title: 'Claims Review - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'cc-support',
        element: withSuspense(CCSupport),
        handle: {
          title: 'CC Support - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'prompt-library',
        element: withSuspense(PromptLibraryPage),
        handle: {
          title: 'Prompt Library - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'dispatch',
        element: withSuspense(DispatchPage),
        handle: {
          title: 'Dispatch - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'access-control',
        element: withSuspense(AccessControlPage),
        handle: {
          title: 'Access Control - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'telemetry-policies',
        element: withSuspense(TelemetryPoliciesPage),
        handle: {
          title: 'Telemetry Policies - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'session-summaries',
        element: withSuspense(SessionSummariesPage),
        handle: {
          title: 'Session Summaries - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'model-capabilities',
        element: withSuspense(ModelCapabilitiesPage),
        handle: {
          title: 'Model Capabilities - Alveary',
        } satisfies RouteHandle,
      },
      {
        path: 'integrations',
        element: withSuspense(IntegrationsPage),
        handle: {
          title: 'Integrations - Alveary',
        } satisfies RouteHandle,
      },
      // Dev-only console-kit gallery (PRD-0201 D-14). The lazy import lives
      // only inside this branch: Vite replaces import.meta.env.DEV with a
      // literal `false` in a production build, so the dead branch — import()
      // call site included — is dropped before bundling, and no gallery
      // chunk is emitted. No ROUTES entry: a typed constant resolving to
      // NotFoundPage in production is worse than a path devs type once.
      ...(import.meta.env.DEV
        ? [
            {
              path: 'design/console',
              element: withSuspense(
                lazy(() => import('@/pages/ConsoleKitGalleryPage')),
              ),
              handle: {
                title: 'Console Kit Gallery - Alveary',
              } satisfies RouteHandle,
            },
          ]
        : []),
      {
        path: '*',
        element: withSuspense(NotFoundPage),
        handle: {
          title: 'Page Not Found - Alveary',
        } satisfies RouteHandle,
      },
    ],
  },
]

// Create and export the router
export const router = createBrowserRouter(routes)
